import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatestWith, Subject, takeUntil } from "rxjs";

import { Organization } from "../../../../organizations/models/organizations.entity";
import { OrganizationStoreService } from "../../../../organizations/services/organization.store.service";
import { talkerCanAccessResources } from "../../../tool-functions/talker-plan-access";
import { Talker } from "../../../../users/models/talker.entity";
import { Role, SubRole } from "../../../../users/models/users.entity";
import { Profile, ProfileService } from "../../../../users/services/profile.service";
import { InPageTab } from "../in-page-tabs/in-page-tabs.component";

@Component({
  selector: 'app-home-page-tabs',
  templateUrl: './home-page-tabs.component.html',
})
export class HomePageTabsComponent implements OnInit, OnDestroy {

  public role!: SubRole;

  public tabs: InPageTab[] = [];

  public organization?: Organization;

  private profile?: Profile;

  private readonly destroy$ = new Subject<void>();

  private get hasPortfolioAccess(): boolean {
    if (this.profile?.role !== Role.Talker) {
      return false;
    }

    return (this.profile.organizationAssociations?.length ?? 0) > 1
      || !!this.organization?.children?.length
      || !!this.organization?.parent
      || !!this.profile.organizationAssociations?.some(
        (association) => !!association.organization?.children?.length,
      );
  }

  private get isAdminViewingOrganization(): boolean {
    return this.profile?.role === Role.Admin && !!this.organization;
  }

  constructor(private readonly profileService: ProfileService,
              private readonly organizationStoreService: OrganizationStoreService,) {
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  ngOnInit() {
    this.profileService.loadProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => undefined,
      });

    this.profileService.getObservable()
      .pipe(
        combineLatestWith(this.organizationStoreService.getObservable()),
        takeUntil(this.destroy$))
      .subscribe(([ profile, organization ]) => {
        this.profile = profile;
        this.role = organization ? 'pilot' : profile.role;
        this.organization = organization;
        this.tabs = this.getTabs();
      });
  }

  private getTabs(): InPageTab[] {
    switch (this.role) {
      case Role.Talker:
        return [ {
          icon: "heart",
          color: "#FF8B05",
          title: "home.cards.mySpaceTitle",
          description: "home.cards.mySpaceDesc",
          link: "my-space"
        },
        {
          icon: "play-button",
          color: "#E37055",
          title: "home.cards.resourcesTitle",
          description: "home.cards.resourcesDesc",
          link: "resources/all"
        },
        {
          icon: "leaves",
          color: "#2CBBB6",
          title: "home.cards.thematicsTitle",
          description: "home.cards.thematicsDesc",
          link: "thematics"
        },
        {
          icon: "calendar",
          color: "#2D7DC8",
          title: "home.cards.trainsTitle",
          description: "home.cards.trainsDescTalker",
          link: "available-trains"
        },
        ].filter((tab) => {
          if (tab.link === 'resources/all') {
            return this.talkerCanAccessResources();
          }
          return true;
        }) as InPageTab[];
      case 'pilot':
        return ([
          this.hasPortfolioAccess ? {
            icon: "building",
            color: "#7A4AA5",
            title: "home.cards.portfolioTitle",
            description: "home.cards.portfolioDesc",
            link: "organizations/portfolio"
          } : null,
          {
            icon: "users",
            color: "#2D7DC8",
            title: "home.cards.collaboratorsTitle",
            description: "home.cards.collaboratorsDesc",
            link: `organizations/${this.organization?.id}/talkers`
          },
          {
            icon: "chart",
            color: "#FF8B05",
            title: "home.cards.statisticsTitle",
            description: "home.cards.statisticsDesc",
            link:  `organizations/${this.organization?.id}/statistics`
          },
          {
            icon: "parameters",
            color: "#2CBBB6",
            title: "home.cards.settingsTitle",
            description: "home.cards.settingsDesc",
            link: `organizations/${this.organization?.id}/edit`
          },
          {
            icon: "crawn",
            color: "#E37055",
            title: "home.cards.offerTitle",
            description: "home.cards.offerDesc",
            link: `organizations/${this.organization?.id}/units`
          },
        ] as Array<InPageTab | null>)
          .filter((tab): tab is InPageTab => tab !== null)
          .filter((tab) => {
            if (this.isAdminViewingOrganization) {
              return true;
            }
            if (tab.link.endsWith('/talkers')) {
              return this.organization?.capabilities?.collaborators !== false;
            }
            if (tab.link.endsWith('/statistics')) {
              return this.organization?.capabilities?.statistics === true;
            }
            if (tab.link.endsWith('/edit')) {
              return this.organization?.capabilities?.organization_settings === true;
            }
            return true;
          });
      case Role.Admin:
        return [ {
          icon: "calendar",
          color: "#FF8B05",
          title: "home.cards.trainsTitle",
          description: "home.cards.trainsDescAdmin",
          link: "trains"
        },
          {
            icon: "building",
            color: "#2D7DC8",
            title: "home.cards.organizationsTitle",
            description: "home.cards.organizationsDesc",
            link: "organizations"
          },
          {
            icon: "leaves",
            color: "#2CBBB6",
            title: "home.cards.thematicsAdminTitle",
            description: "home.cards.thematicsAdminDesc",
            link: "thematics"
          },
          {
            icon: "users",
            color: "#E37055",
            title: "home.cards.usersTitle",
            description: "home.cards.usersDesc",
            link: "users"
          },
          {
            icon: "chart",
            color: "#ca81e7",
            title: "home.cards.dashboardTitle",
            description: "home.cards.dashboardDesc",
            link: "statistics"
          },

        ];
      case Role.Sherpa:
        return [ {
          icon: "calendar",
          color: "#6AA33E",
          title: "home.cards.trainsTitle",
          description: "home.cards.trainsDescSherpa",
          link: "my-trains"
        },
          {
            icon: "chart",
            color: "#FF8B05",
            title: "home.cards.statisticsTitle",
            description: "home.cards.statisticsDesc",
            link:  "statistics"
          },
          {
            icon: "parameters",
            color: "#2CBBB6",
            title: "home.cards.sherpaProfileTitle",
            description: "home.cards.sherpaProfileDesc",
            link: "my-profile"
          },
          {
            icon: "bubble-smile",
            color: "#2D7DC8",
            title: "home.cards.contactTitle",
            description: "home.cards.contactDesc",
            link: "questions"
          }, ];
      default:
        return [];
    }
  }

  private talkerCanAccessResources(): boolean {
    if (this.profile?.role !== Role.Talker) {
      return false;
    }

    return talkerCanAccessResources(this.profile as Talker);
  }
}
